import { z } from "zod";
import { type ComparisonDimension, ComparisonDimensions, toComparisonGroupFilters } from "#shared/comparisonDimension.ts";
import { type MemberFilters, MemberFiltersSchema } from "@domains/reviews/shared/reviewee-filters/memberFilters.ts";
import { TENURE_BUCKET_KEYS, toTenureFilters } from "@domains/reviews/shared/reviewee-filters/tenureBuckets.ts";
import { PeriodSelectionSchema, toPeriodKey } from "#shared/periodSelection.ts";
export const MAX_COMPARISON_SELECTIONS = 9;

const ComparisonBreakdownSchema = z.object({
    dimension: z.enum(ComparisonDimensions),
    groupKeys: z.array(z.string().min(1)).max(MAX_COMPARISON_SELECTIONS)
});
export type ComparisonBreakdown = z.infer<typeof ComparisonBreakdownSchema>;

const ComparisonSelectionSchema = z.object({
    id: z.string().min(1),
    label: z.string().optional(),
    breakdown: ComparisonBreakdownSchema.optional(),
    ...PeriodSelectionSchema.shape,
    ...MemberFiltersSchema.shape,
    tenure: z.enum(TENURE_BUCKET_KEYS).optional()
});
export type ComparisonSelection = z.infer<typeof ComparisonSelectionSchema>;

export const ComparisonSelectionsSchema = z.array(ComparisonSelectionSchema).max(MAX_COMPARISON_SELECTIONS);

export type ComparisonInput = Omit<ComparisonSelection, "id" | "label" | "breakdown">;

export function resolveTenureWindow(input: ComparisonInput, now: Date): ComparisonInput {
    if (!input.tenure) {
        return input;
    }

    const { tenure: _band, ...rest } = input;
    const band = toTenureFilters(input.tenure, now);

    return { ...rest, hireDateMin: band?.hireDateMin, hireDateMax: band?.hireDateMax };
}

export function toComparisonInput(selection: ComparisonSelection): ComparisonInput {
    const { id: _id, label: _label, breakdown: _breakdown, ...rest } = selection;

    return Object.fromEntries(Object.entries(rest).filter(([, value]) => value !== undefined && !(Array.isArray(value) && value.length === 0)));
}

export function mergeComparisonSelection(base: ComparisonInput, comparison: ComparisonSelection): ComparisonInput {
    return { ...base, ...toComparisonInput(comparison) };
}

export function toIndependentSelection(comparison: ComparisonSelection): ComparisonInput {
    return toComparisonInput(comparison);
}

export interface ComparisonTreeBase {
    id: string;
    input: ComparisonInput;
    breakdown?: ComparisonBreakdown;
}

export type FlattenedComparison = { kind: "comparison"; id: string; label?: string; input: ComparisonInput } | { kind: "group"; id: string; parentId: string; dimension: ComparisonDimension; groupKey: string; input: ComparisonInput };

export function toGroupSeriesId(parentId: string, dimension: ComparisonDimension, groupKey: string): string {
    return `${parentId}:${dimension}:${groupKey}`;
}

function toGroupSeries(parentId: string, parentInput: ComparisonInput, breakdown: ComparisonBreakdown): FlattenedComparison[] {
    return breakdown.groupKeys.flatMap(groupKey => {
        const filters = toComparisonGroupFilters(breakdown.dimension, groupKey);

        if (!filters) {
            return [];
        }

        return [
            {
                kind: "group" as const,
                id: toGroupSeriesId(parentId, breakdown.dimension, groupKey),
                parentId,
                dimension: breakdown.dimension,
                groupKey,
                input: { ...parentInput, ...filters }
            }
        ];
    });
}

export function flattenComparisonTree(base: ComparisonTreeBase, comparisons: ComparisonSelection[]): FlattenedComparison[] {
    const series: FlattenedComparison[] = base.breakdown ? toGroupSeries(base.id, base.input, base.breakdown) : [];

    for (const comparison of comparisons) {
        const input = toIndependentSelection(comparison);

        series.push({ kind: "comparison", id: comparison.id, label: comparison.label, input });

        if (comparison.breakdown) {
            series.push(...toGroupSeries(comparison.id, input, comparison.breakdown));
        }
    }

    return series.slice(0, MAX_COMPARISON_SELECTIONS);
}

const MEMBER_FILTER_FIELDS = Object.keys(MemberFiltersSchema.shape) as (keyof MemberFilters)[];

function toAudienceKey(selection: ComparisonInput): string {
    return JSON.stringify(
        MEMBER_FILTER_FIELDS.map(field => {
            const value = selection[field];

            return Array.isArray(value) ? value.toSorted() : (value ?? null);
        })
    );
}

function hasPeriodVariation(selections: ComparisonInput[]): boolean {
    return new Set(selections.map(toPeriodKey)).size > 1;
}

export function differsOnlyByPeriod(selections: ComparisonInput[]): boolean {
    return new Set(selections.map(toAudienceKey)).size === 1 && hasPeriodVariation(selections);
}
